import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Church, Users, Home, LogOut, Sun, Moon,
  DollarSign, UsersRound, CalendarDays, Megaphone, BadgeCheck, Video,
  ShieldAlert, BarChart3,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { ROLE_GATING_IS_UI_ONLY } from '../roles';

// `section` keys match roles.js — that table decides which of these show.
const navItems = [
  { section: 'members', to: '/', label: 'Members', icon: Users, end: true },
  { section: 'households', to: '/households', label: 'Households', icon: Home, end: false },
  { section: 'finance', to: '/finance', label: 'Finance', icon: DollarSign, end: false },
  { section: 'financials', to: '/financials', label: 'Financials', icon: BarChart3, end: false },
  { section: 'groups', to: '/groups', label: 'Groups', icon: UsersRound, end: false },
  { section: 'events', to: '/events', label: 'Events', icon: CalendarDays, end: false },
  { section: 'comms', to: '/comms', label: 'Comms', icon: Megaphone, end: false },
  { section: 'checkin', to: '/checkin', label: 'Check-in', icon: BadgeCheck, end: false },
  { section: 'content', to: '/content', label: 'Sermons', icon: Video, end: false },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { theme, toggle } = useTheme();

  const visibleNav = navItems.filter(({ section }) => can(section));

  return (
    <div className="min-h-screen flex flex-col">
      {ROLE_GATING_IS_UI_ONLY && (
        <div className="bg-amber-100 dark:bg-amber-900/40 border-b border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200">
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-start gap-2 text-xs">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>Demonstration.</strong> Menus are filtered by role to show
              how access will work. Role-based access is not yet enforced on the
              server, so this is a preview of the design, not a security
              control.
            </span>
          </div>
        </div>
      )}

      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 text-brand-700 dark:text-brand-100 font-bold">
            <Church size={22} />
            <span>Church Workflow</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 dark:text-slate-400 hidden sm:inline">
              {user?.full_name || user?.email}{' '}
              <span className="text-slate-400 dark:text-slate-500">({user?.role})</span>
            </span>
            <button
              onClick={toggle}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              className="text-slate-500 dark:text-slate-300 hover:text-brand-600 dark:hover:text-brand-300"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              onClick={logout}
              className="text-slate-500 dark:text-slate-300 hover:text-red-600 flex items-center gap-1 text-sm"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {visibleNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                  isActive
                    ? 'border-brand-600 text-brand-700 dark:text-brand-300'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'
                }`
              }
            >
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
